import React, { useCallback, useEffect, useRef } from 'react';
import { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import EmojiPicker from '@/components/communication/messages/emojiPicker/empjiPicker';

import styles from './LomavisCampaignBuilder1.module.css';
import {
  getPrimaryTextEditorValue,
  primaryTextQuillFormats,
  primaryTextQuillModules,
  setPrimaryTextEditorValue
} from './primaryTextQuill';

const getEditorSelection = (editor: any) => {
  if (typeof editor.getSelection !== 'function') return null;

  try {
    return editor.getSelection();
  } catch {
    return null;
  }
};

const editorHasFocus = (editor: any) => {
  if (getEditorSelection(editor)) return true;
  if (typeof editor.hasFocus === 'function') return editor.hasFocus();

  const activeElement = document.activeElement;
  return Boolean(
    activeElement &&
      (editor.root === activeElement || editor.root?.contains(activeElement))
  );
};

export const LomavisCampaignPrimaryTextEditor1: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const lastSyncedValueRef = useRef(value || '');
  const pendingExternalValueRef = useRef<string | null>(null);
  const isEditorFocusedRef = useRef(false);
  const isSettingValueRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const syncEditorValue = useCallback((editor: any, nextValue: string) => {
    isSettingValueRef.current = true;
    setPrimaryTextEditorValue(editor, nextValue);
    isSettingValueRef.current = false;
    lastSyncedValueRef.current = nextValue;
  }, []);

  useEffect(() => {
    if (!containerRef.current || editorRef.current) return undefined;

    const editor = new Quill(containerRef.current, {
      formats: primaryTextQuillFormats,
      modules: primaryTextQuillModules,
      theme: 'snow'
    });

    editorRef.current = editor;
    syncEditorValue(editor, lastSyncedValueRef.current);

    const handleTextChange = (_delta: any, _oldDelta: any, source: string) => {
      if (isSettingValueRef.current || source === 'api') return;

      const nextValue = getPrimaryTextEditorValue(editor);
      lastSyncedValueRef.current = nextValue;
      pendingExternalValueRef.current = null;
      onChangeRef.current(nextValue);
    };

    const handleSelectionChange = (range: any) => {
      isEditorFocusedRef.current = Boolean(range);
      if (range) return;

      const pendingExternalValue = pendingExternalValueRef.current;
      pendingExternalValueRef.current = null;

      if (
        pendingExternalValue !== null &&
        pendingExternalValue !== lastSyncedValueRef.current
      ) {
        syncEditorValue(editor, pendingExternalValue);
      }
    };

    editor.on('text-change', handleTextChange);
    editor.on('selection-change', handleSelectionChange);

    return () => {
      editor.off('text-change', handleTextChange);
      editor.off('selection-change', handleSelectionChange);
      editor.getModule('toolbar')?.container?.remove();
      editorRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        delete (containerRef.current as any).__quill;
      }
    };
  }, [syncEditorValue]);

  useEffect(() => {
    const editor = editorRef.current;
    const nextValue = value || '';

    if (!editor || nextValue === lastSyncedValueRef.current) return;

    if (isEditorFocusedRef.current || editorHasFocus(editor)) {
      pendingExternalValueRef.current = nextValue;
      return;
    }

    syncEditorValue(editor, nextValue);
  }, [syncEditorValue, value]);

  const insertEmoji = useCallback((emoji: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const selection = getEditorSelection(editor);
    const index: number = selection?.index ?? editor.getLength();
    editor.insertText(index, emoji, 'user');
    editor.setSelection(index + emoji.length, 0, 'user');
  }, []);

  return (
    <div className={styles.primaryTextEditor}>
      <div ref={containerRef} />
      <div className="d-flex justify-content-start mt-2">
        <EmojiPicker addEmoji={insertEmoji} />
      </div>
    </div>
  );
};
